/**
 * Reseller Management Page
 * Master Admin can create and manage reseller accounts
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "../../components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";

const formatDate = (isoString) => {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// Removed unused `formatCurrency` helper (was a leftover from an earlier
// design with margin-currency display). Re-add when reseller economics
// columns are surfaced again.

const ResellerManagementPage = ({ onNavigate }) => {
  const { apiFetch, user } = useAuth();
  const { success: showToast, error: showError } = useToast();
  
  const [resellers, setResellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  
  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    reseller_name: "",
    admin_email: "",
    contact_name: "",
    phone: "",
    invite_mode: "invite",
    temp_password: "",
    confirm_password: "",
  });
  const [createSuccess, setCreateSuccess] = useState(null);

  // Remove reseller modal state
  const [resellerToRemove, setResellerToRemove] = useState(null); // {id, name, dealers_assigned_count}
  const [removing, setRemoving] = useState(false);

  const handleRemoveReseller = async () => {
    if (!resellerToRemove) return;
    setRemoving(true);
    try {
      const res = await apiFetchRef.current(`/admin/resellers/${resellerToRemove.id}`, {
        method: "DELETE",
      });
      showToast(res.data?.message || "Reseller removed");
      setResellerToRemove(null);
      await doFetch(search);
    } catch (e) {
      const detail = e.response?.data?.detail || "Failed to remove reseller";
      showError(detail);
      // Keep modal open so user can read the error. They can close with Cancel.
    } finally {
      setRemoving(false);
    }
  };

  // Use ref so async closures always have fresh apiFetch
  const apiFetchRef = useRef(apiFetch);
  useEffect(() => { apiFetchRef.current = apiFetch; });

  const doFetch = useCallback(async (searchVal) => {
    try {
      setLoading(true);
      const params = searchVal ? `?search=${encodeURIComponent(searchVal)}` : "";
      const response = await apiFetchRef.current(`/admin/resellers${params}`);
      if (!response.data || !Array.isArray(response.data.resellers)) {
        console.error('[RESELLERS] Invalid response shape:', response.data);
        setResellers([]);
        return;
      }
      setResellers(response.data.resellers);
    } catch (err) {
      console.error(`[RESELLERS] Fetch failed:`, err);
    } finally {
      setLoading(false);
    }
  }, []); // stable — uses ref for apiFetch

  const fetchResellers = useCallback(() => doFetch(search), [doFetch, search]);

  // Initial load
  useEffect(() => { doFetch(""); }, [doFetch]);

  // Re-fetch on search change
  useEffect(() => {
    if (search !== "") doFetch(search);
  }, [search, doFetch]);

  const handleCreateReseller = async () => {
    // Validation
    if (!createForm.reseller_name.trim()) {
      showError("Reseller name is required");
      return;
    }
    if (!createForm.admin_email.trim()) {
      showError("Admin email is required");
      return;
    }
    if (createForm.invite_mode === "temp_password") {
      if (!createForm.temp_password || createForm.temp_password.length < 8) {
        showError("Password must be at least 8 characters");
        return;
      }
      if (createForm.temp_password !== createForm.confirm_password) {
        showError("Passwords do not match");
        return;
      }
    }

    try {
      setCreating(true);
      const response = await apiFetch("/admin/resellers", {
        method: "POST",
        data: {
          reseller_name: createForm.reseller_name.trim(),
          admin_email: createForm.admin_email.trim().toLowerCase(),
          contact_name: createForm.contact_name.trim() || null,
          phone: createForm.phone.trim() || null,
          invite_mode: createForm.invite_mode,
          temp_password: createForm.invite_mode === "temp_password" ? createForm.temp_password : null,
        },
      });

      setCreateSuccess(response.data);
      showToast("Reseller created successfully");
      fetchResellers();
    } catch (err) {
      showError(err.response?.data?.detail || "Failed to create reseller");
    } finally {
      setCreating(false);
    }
  };

  const resetCreateForm = () => {
    setCreateForm({
      reseller_name: "",
      admin_email: "",
      contact_name: "",
      phone: "",
      invite_mode: "invite",
      temp_password: "",
      confirm_password: "",
    });
    setCreateSuccess(null);
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    resetCreateForm();
  };

  // RBAC check
  if (user?.role !== "MASTER_ADMIN") {
    return (
      <div className="p-8">
        <Card>
          <CardContent className="py-16 text-center">
            <div className="text-6xl mb-4">🚫</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600">You don&apos;t have permission to access this page.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 w-full" data-testid="reseller-management-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reseller Management</h1>
          <p className="text-gray-600 mt-1">{resellers.length} resellers registered</p>
          <p className="text-xs text-gray-400 mt-0.5">Showing: Resellers only</p>
        </div>
        <Button 
          onClick={() => setShowCreateModal(true)} 
          className="bg-indigo-600 hover:bg-indigo-700"
          data-testid="create-reseller-btn"
        >
          <span className="mr-2">+</span> Create Reseller
        </Button>
      </div>

      {/* Search */}
      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="flex gap-4 items-center">
            <div className="flex-1">
              <Input
                placeholder="Search by name or contact..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="max-w-md"
                data-testid="reseller-search"
              />
            </div>
            <Button variant="outline" onClick={fetchResellers}>Refresh</Button>
          </div>
        </CardContent>
      </Card>

      {/* Resellers Table */}
      <Card>
        <CardHeader>
          <CardTitle>Resellers</CardTitle>
          <CardDescription>Manage reseller accounts and their dealer assignments</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="overflow-x-auto" data-testid="resellers-table-skeleton">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left text-sm text-gray-500">
                    <th className="pb-3 font-medium">Reseller</th>
                    <th className="pb-3 font-medium">Admin</th>
                    <th className="pb-3 font-medium">Dealers</th>
                    <th className="pb-3 font-medium">Status</th>
                    <th className="pb-3 font-medium">Created</th>
                    <th className="pb-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {[0, 1, 2, 3].map((i) => (
                    <tr key={i}>
                      <td className="py-4"><Skeleton className="h-4 w-40" /></td>
                      <td className="py-4"><Skeleton className="h-4 w-48" /></td>
                      <td className="py-4"><Skeleton className="h-6 w-20 rounded-full" /></td>
                      <td className="py-4"><Skeleton className="h-6 w-16 rounded-full" /></td>
                      <td className="py-4"><Skeleton className="h-4 w-24" /></td>
                      <td className="py-4 text-right"><Skeleton className="h-8 w-24 ml-auto" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : resellers.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg">No resellers yet</p>
              <p className="text-sm mt-2">Create your first reseller to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left text-sm text-gray-500">
                    <th className="pb-3 font-medium">Reseller</th>
                    <th className="pb-3 font-medium">Admin</th>
                    <th className="pb-3 font-medium">Dealers</th>
                    <th className="pb-3 font-medium">Status</th>
                    <th className="pb-3 font-medium">Created</th>
                    <th className="pb-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {resellers.map((reseller) => (
                    <tr 
                      key={reseller.id} 
                      className="hover:bg-gray-50"
                      data-testid={`reseller-row-${reseller.id}`}
                    >
                      <td className="py-4">
                        <div>
                          <p className="font-medium text-gray-900">{reseller.name}</p>
                          {reseller.contact_name && (
                            <p className="text-sm text-gray-500">{reseller.contact_name}</p>
                          )}
                        </div>
                      </td>
                      <td className="py-4">
                        <p className="text-sm text-gray-700">{reseller.admin_email || "No admin"}</p>
                      </td>
                      <td className="py-4">
                        <Badge variant="secondary">{reseller.dealers_assigned_count} dealers</Badge>
                      </td>
                      <td className="py-4">
                        <Badge className={reseller.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}>
                          {reseller.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </td>
                      <td className="py-4 text-sm text-gray-500">
                        {formatDate(reseller.created_at)}
                      </td>
                      <td className="py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => onNavigate?.("admin-reseller-detail", reseller.id)}
                            data-testid={`view-reseller-${reseller.id}`}
                          >
                            View
                          </Button>
                          <Button
                            size="sm"
                            className="bg-indigo-600 hover:bg-indigo-700"
                            onClick={() => {
                              onNavigate?.("admin-reseller-detail", reseller.id);
                              // Will open create dealer modal after navigation
                            }}
                            data-testid={`add-dealer-${reseller.id}`}
                          >
                            + Add Dealer
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                            onClick={() => setResellerToRemove({
                              id: reseller.id,
                              name: reseller.name,
                              dealers_assigned_count: reseller.dealers_assigned_count || 0,
                            })}
                            data-testid={`remove-reseller-${reseller.id}`}
                          >
                            Remove
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Reseller Modal */}
      <Dialog open={showCreateModal} onOpenChange={handleCloseModal}>
        <DialogContent className="sm:max-w-[500px]">
          {createSuccess ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-green-600">✓ Reseller Created</DialogTitle>
                <DialogDescription>
                  The reseller account has been created successfully.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="font-medium text-green-800">{createSuccess.reseller_name}</p>
                  <p className="text-sm text-green-700 mt-1">{createSuccess.admin_email}</p>
                </div>
                
                {createSuccess.auth_mode === "invite" && (
                  <p className="text-sm text-gray-600">
                    An invitation email has been sent to the admin.
                    {!createSuccess.email_sent && (
                      <span className="text-orange-600 block mt-1">
                        ⚠️ Email delivery failed. Share the invite link manually.
                      </span>
                    )}
                  </p>
                )}
                
                {createSuccess.auth_mode === "temp_password" && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-amber-800">Temporary Password Set</p>
                    <p className="text-sm text-amber-700 mt-1">
                      The user can log in immediately with the email and password you provided.
                    </p>
                  </div>
                )}
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={handleCloseModal}>
                  Close
                </Button>
                <Button onClick={() => {
                  handleCloseModal();
                  onNavigate?.("admin-reseller-detail", createSuccess.reseller_id);
                }}>
                  Go to Reseller
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Create Reseller</DialogTitle>
                <DialogDescription>
                  Create a new reseller account. They will be able to view assigned dealers and margins.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="reseller_name">Reseller Name *</Label>
                  <Input
                    id="reseller_name"
                    placeholder="e.g., CrossRoads Marketing"
                    value={createForm.reseller_name}
                    onChange={(e) => setCreateForm(f => ({ ...f, reseller_name: e.target.value }))}
                    data-testid="reseller-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="admin_email">Admin Email *</Label>
                  <Input
                    id="admin_email"
                    type="email"
                    placeholder="admin@reseller.com"
                    value={createForm.admin_email}
                    onChange={(e) => setCreateForm(f => ({ ...f, admin_email: e.target.value }))}
                    data-testid="reseller-email-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact_name">Contact Name</Label>
                    <Input
                      id="contact_name"
                      placeholder="John Doe"
                      value={createForm.contact_name}
                      onChange={(e) => setCreateForm(f => ({ ...f, contact_name: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      placeholder="(555) 123-4567"
                      value={createForm.phone}
                      onChange={(e) => setCreateForm(f => ({ ...f, phone: e.target.value }))}
                    />
                  </div>
                </div>
                
                <div className="space-y-3">
                  <Label>Authentication Setup</Label>
                  <RadioGroup 
                    value={createForm.invite_mode} 
                    onValueChange={(v) => setCreateForm(f => ({ ...f, invite_mode: v }))}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="invite" id="invite" />
                      <Label htmlFor="invite" className="font-normal cursor-pointer">
                        Send invite email (recommended)
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="temp_password" id="temp_password" />
                      <Label htmlFor="temp_password" className="font-normal cursor-pointer">
                        Set temporary password
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
                
                {createForm.invite_mode === "temp_password" && (
                  <div className="space-y-4 pl-4 border-l-2 border-gray-200">
                    <div className="space-y-2">
                      <Label htmlFor="temp_password">Password</Label>
                      <Input
                        id="temp_password"
                        type="password"
                        placeholder="Min 8 characters"
                        value={createForm.temp_password}
                        onChange={(e) => setCreateForm(f => ({ ...f, temp_password: e.target.value }))}
                        data-testid="temp-password-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirm_password">Confirm Password</Label>
                      <Input
                        id="confirm_password"
                        type="password"
                        placeholder="Confirm password"
                        value={createForm.confirm_password}
                        onChange={(e) => setCreateForm(f => ({ ...f, confirm_password: e.target.value }))}
                        data-testid="confirm-password-input"
                      />
                    </div>
                  </div>
                )}
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={handleCloseModal} disabled={creating}>
                  Cancel
                </Button>
                <Button onClick={handleCreateReseller} disabled={creating} data-testid="submit-create-reseller">
                  {creating ? "Creating..." : "Create Reseller"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Remove Reseller Confirmation Modal */}
      <Dialog open={!!resellerToRemove} onOpenChange={(open) => { if (!open) setResellerToRemove(null); }}>
        <DialogContent className="sm:max-w-[440px]" data-testid="remove-reseller-modal">
          <DialogHeader>
            <DialogTitle className="text-red-700">Remove Reseller</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove <span className="font-semibold">{resellerToRemove?.name}</span>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {resellerToRemove?.dealers_assigned_count > 0 && (
            <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="remove-reseller-warning">
              <p className="font-medium">
                This reseller has {resellerToRemove.dealers_assigned_count} active dealer{resellerToRemove.dealers_assigned_count === 1 ? "" : "s"} assigned.
              </p>
              <p className="mt-1 text-red-600">
                You must reassign these dealers before you can remove this reseller.
              </p>
            </div>
          )}
          <DialogFooter className="mt-2">
            <Button
              variant="outline"
              onClick={() => setResellerToRemove(null)}
              disabled={removing}
              data-testid="remove-reseller-cancel"
            >
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleRemoveReseller}
              disabled={removing}
              data-testid="remove-reseller-confirm"
            >
              {removing ? "Removing..." : "Confirm Remove"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResellerManagementPage;
